import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Users, TrendingUp } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Compact quota indicator shown in the sidebar for org owners
 * (property managers). Displays properties + staff usage against plan
 * caps with color-coded bars. Nudges the user to upgrade when nearing
 * the cap.
 */
export const PlanQuotaIndicator = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Only managers/owners see the quota indicator. Staff don't manage
    // subscriptions.
    if (!user || !['property_manager', 'owner'].includes(user.role)) return;
    let alive = true;
    api.get('/plan-limits')
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, [user]);

  if (error || !data || !user || !['property_manager', 'owner'].includes(user.role)) {
    return null;
  }

  const rows = [
    {
      key: 'properties',
      label: 'Properties',
      icon: Building2,
      cap: data.limits?.properties,
      used: data.usage?.properties ?? 0,
    },
    {
      key: 'staff',
      label: 'Staff seats',
      icon: Users,
      cap: data.limits?.staff,
      used: data.usage?.staff ?? 0,
    },
  ];

  // Nearing-cap threshold: 80% used
  const nearCap = rows.some(
    (r) => r.cap != null && r.cap > 0 && r.used / r.cap >= 0.8
  );

  return (
    <div
      className="mx-3 mb-3 rounded-lg bg-slate-800/60 border border-slate-700/60 p-3"
      data-testid="plan-quota-indicator"
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">
          Plan usage
        </p>
        {nearCap && (
          <Link
            to="/billing"
            className="text-[10px] text-amber-300 hover:text-amber-200 font-semibold inline-flex items-center gap-0.5"
            data-testid="quota-upgrade-link"
          >
            <TrendingUp className="h-2.5 w-2.5" />
            Upgrade
          </Link>
        )}
      </div>
      <div className="space-y-2">
        {rows.map((r) => {
          const unlimited = r.cap == null;
          const pct = unlimited ? 0 : Math.min(100, Math.round((r.used / Math.max(1, r.cap)) * 100));
          const tone =
            unlimited
              ? 'bg-emerald-400/70'
              : pct >= 100
                ? 'bg-red-400'
                : pct >= 80
                  ? 'bg-amber-400'
                  : 'bg-sky-400/80';
          return (
            <div key={r.key}>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-300 flex items-center gap-1.5">
                  <r.icon className="h-3 w-3 text-slate-400" />
                  {r.label}
                </span>
                <span className="text-slate-400 tabular-nums">
                  {r.used}
                  <span className="text-slate-500"> / {unlimited ? '∞' : r.cap}</span>
                </span>
              </div>
              {!unlimited && (
                <div className="mt-1 h-1 rounded-full bg-slate-700/60 overflow-hidden">
                  <div
                    className={`h-full ${tone} transition-all`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlanQuotaIndicator;
